import React, { useState, useEffect, useRef } from 'react';
import dayjs from "dayjs";
/*
  Hook that wraps the datetimepicker library used in the assets to keep the system consistent on webpacker/asset implemented JS.
  Handles value changing on the JS events provided by the library.

  Parameters: 
    - initialValue <dayjs date object>
    - conditions to trigger useEffect
    - options:
      { format: <dayjs_format> }
  Return value: Array [<selectedDate>, <DOM identifier>]
  Expects: The returned DOM identifier is added to the input field
*/

const randomId = () => `datetimepicker-${Math.round(Math.random() * 10000000)}`
const HOUR_MINUTE_FORMAT_REGEX = new RegExp('((h|H){1,2}):((m|M){1,2})');
const SECONDS_FORMAT_REGEX = new RegExp('((h|H){1,2}):((m|M){1,2}):((s|S){1,2})');

const loadLocale = async () => {
  let locale;
  switch (document.body.dataset.locale) {
    case 'fi':
      locale = (await import('flatpickr/dist/l10n/fi.js')).Finnish;
      break;
    case 'sv':
      locale = (await import('flatpickr/dist/l10n/sv.js')).Swedish;
      break;
    case 'fr':
      locale = (await import('flatpickr/dist/l10n/fr.js')).French;
      break;
    default:
      locale = 'default';
  }
  return locale;
}

const useDateTimePicker = (initialValue, conditions, options = {}, overlay = false) => {
  const currentFormat = options.format || 'YYYY-MM-DD';
  const [datetime, setDatetime] = useState(initialValue ? dayjs(new Date(initialValue)).format(options.altFormat ? options.altFormat : currentFormat) : initialValue);
  const handleDateChange = (date) => setDatetime(date);
  // Use memo to keep the id consistent
  const id = React.useMemo(() => randomId(), []);
  const selector = '#' + id
  const initialized = useRef(false);
  const effectDependencies = Array.isArray(conditions)
  ? conditions
  : (conditions === undefined ? [] : [conditions]);

  useEffect(() => {
    let datePicker;
    let observer;

    const initFlatPickr = async (el) => {
      if (!initialized.current) {
        const activeLocale = await loadLocale();
        datePicker = el.flatpickr({
          locale: activeLocale,
          ...(options.monthYearPicker ? {
              plugins: [
                monthSelectPlugin({shorthand: true, dateFormat: "Y-m", altFormat: "F Y"})
              ]
            } : {
              ...options,
              enableTime: currentFormat.match(HOUR_MINUTE_FORMAT_REGEX) ? true : false,
              enableSeconds: options.altInput ? (options.altFormat || "YYYY-MM-DD").match(SECONDS_FORMAT_REGEX) : (currentFormat.match(SECONDS_FORMAT_REGEX) ? true : false),
              dateFormat: currentFormat || 'YYYY-MM-DD',
              parseDate: (datestr, format) => {
                return dayjs(datestr, format, true).toDate();
              },
              formatDate: (date, format, locale) => {
                return dayjs(date).format(format);
              },
            }
          ),
          onChange: (selectedDates, dateStr, instance) => {
            handleDateChange(dateStr)
          },
          allowInput: true,
        })
        initialized.current = true;
      }
    };

    const el = document.querySelector(selector);
    if (el) {
      el.focus({preventScroll: true});
      initFlatPickr(el);
    } else {
      // Waiting for modals - Observer is used only when element is not found
      observer = new MutationObserver(() => {
        const elCheck = document.querySelector(selector);
        if (elCheck) {
          elCheck.focus({preventScroll: true});
          initFlatPickr(elCheck);
          observer.disconnect(); // stop once initialized
        }
      });

      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }

    // Remove event listener on unmount
    return () => {
      if (datePicker && initialized.current) {
        datePicker.destroy();
        initialized.current = false;
      }

      if (observer) observer.disconnect();
    };
  }, effectDependencies)

  return [datetime, id]
}

export default useDateTimePicker;